// ./src/hooks/useTicketTypeCoupons.ts
//
// Loads a ticket type's coupon grants (active + disabled) and exposes the three
// mutations its editor needs. The drawer edits rows as a draft and commits them on
// Save (mirroring the event's RSVP rules, which are one settings blob), so there's
// no per-row in-flight state here — the drawer's Save button owns the spinner.
// Skips loading until a ticketTypeId is provided (the drawer only mounts for a
// chosen ticket).

import { useCallback, useEffect, useRef, useState } from "react";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { TicketTypeCouponService, TicketTypeCoupon, TicketTypeCouponInput } from "@/utils/api";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

export interface UseTicketTypeCouponsReturn {
  grants: TicketTypeCoupon[];
  loading: boolean;
  /**
   * The ticketTypeId whose rows are currently in `grants` — null while a load is
   * in flight. An editor seeding a draft from `grants` must wait for this to equal
   * its ticketTypeId; a bare "isLoaded" boolean stays true from the previous load
   * and lets the editor seed from an empty list before the real rows land.
   */
  loadedFor: string | null;
  error: OpResult | null;
  reload: () => Promise<void>;

  addGrant: (params: TicketTypeCouponInput) => Promise<OpResult>;
  updateGrant: (id: string, params: TicketTypeCouponInput) => Promise<OpResult>;
  removeGrant: (id: string) => Promise<OpResult>;
}

export const useTicketTypeCoupons = (ticketTypeId?: string | null): UseTicketTypeCouponsReturn => {
  const [grants, setGrants] = useState<TicketTypeCoupon[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [error, setError] = useState<OpResult | null>(null);

  const callIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!ticketTypeId) {
      setGrants([]);
      setLoadedFor(null);

      return;
    }

    const myCall = ++callIdRef.current;
    setLoading(true);
    // Nothing loaded for this ticket yet — cleared so a consumer can't mistake the
    // previous ticket's (or an empty) list for this one's.
    setLoadedFor(null);
    setError(null);

    const result = await TicketTypeCouponService.list(ticketTypeId, [STATUSES.ACTIVE, STATUSES.DISABLED]);

    if (myCall !== callIdRef.current) {
      return;
    }

    setLoading(false);

    if (result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
      setGrants(result.data as TicketTypeCoupon[]);
      setLoadedFor(ticketTypeId);
    } else {
      setError(result);
      setGrants([]);
    }
  }, [ticketTypeId]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(load, loadedFor !== null);

  useEffect(() => {
    load();
  }, [load]);

  const addGrant = useCallback(async (params: TicketTypeCouponInput): Promise<OpResult> => {
    const result = await TicketTypeCouponService.create(params);

    if (result.didSucceed()) {
      const created = Array.isArray(result.data) ? (result.data as TicketTypeCoupon[]) : [];
      setGrants((prev) => [...prev, ...created]);
    }

    return result;
  }, []);

  const updateGrant = useCallback(async (id: string, params: TicketTypeCouponInput): Promise<OpResult> => {
    const result = await TicketTypeCouponService.update(id, params);

    if (result.didSucceed()) {
      // Patch the row from the returned record rather than reloading the list.
      const updated = Array.isArray(result.data) ? (result.data as TicketTypeCoupon[]) : [];
      const byId = new Map(updated.map((g) => [g.id, g]));
      setGrants((prev) => prev.map((g) => (g.id && byId.has(g.id) ? { ...g, ...byId.get(g.id) } : g)));
    }

    return result;
  }, []);

  const removeGrant = useCallback(async (id: string): Promise<OpResult> => {
    const result = await TicketTypeCouponService.remove(id);

    if (result.didSucceed()) {
      setGrants((prev) => prev.filter((g) => g.id !== id));
    }

    return result;
  }, []);

  return {
    grants,
    loading,
    loadedFor,
    error,
    reload: load,
    addGrant,
    updateGrant,
    removeGrant,
  };
};
